/**
 * Typed mock of the identity contract (§16.2 — build against contracts with
 * mocks; the UI never waits for backend). Active only when VITE_API_BASE_URL
 * is unset; flipping that env var swaps in the real fetch transport with ZERO
 * app-code changes.
 *
 * Faithful to identity.yaml v1.1.0:
 *  - Wrapped error envelope with stable codes + AR/EN messages (x-error-codes).
 *  - Idempotency-Key required on mutations (400 IDEMPOTENCY_KEY_MISSING).
 *  - **ADMIN_WEB cookie flow (v1.1.0)**: login/refresh SET the refresh token
 *    as the (emulated) httpOnly cookie and OMIT `refresh_token` from the JSON
 *    body; `/auth/token/refresh` accepts the token from the body OR the
 *    cookie — body wins when both are present; neither → 401 REFRESH_INVALID.
 *    Non-ADMIN_WEB chains keep the v1.0.0 body pair.
 *  - Single-use rotating refresh tokens; reuse → 401 REFRESH_REUSE_DETECTED
 *    and the whole chain is revoked.
 *  - Same AUTH_INVALID_CREDENTIALS for unknown phone vs wrong password (§11.1).
 *
 * Cookie emulation: production ADMIN_WEB delivers the refresh token as an
 * httpOnly cookie the browser attaches automatically. The mock stands in for
 * that cookie with a sessionStorage entry OWNED BY THIS MOCK — app code never
 * reads or writes it, so deleting the mock deletes the emulation. Tokens are
 * mock literals with zero real-world value.
 */

import type { Transport, TransportRequest, TransportResponse } from "../../api/transport";
import type { AuthResult, Me, TokenPair, User } from "../contract";
import { PHONE_PATTERN } from "../contract";

export const MOCK_ADMIN = {
  phone: "+9647701234567",
  password: "Mashe!Admin2026",
  name: "نور الإدارة",
} as const;

const ACCESS_TTL_SECONDS = 900;
const EMULATED_COOKIE_KEY = "admin.mock.refresh-cookie";

/**
 * Login throttling emulation (identity.yaml: 429 RATE_LIMITED + Retry-After
 * on /auth/login). The real limiter lives at the gateway; the mock locks
 * after N consecutive failures so the §11.1 lockout/cooldown UI is testable.
 */
export const LOGIN_LOCKOUT_THRESHOLD = 5;
export const LOGIN_LOCKOUT_SECONDS = 90;

/**
 * The effective permission set of the seeded platform admin — the §14.4 view
 * keys of every merged admin surface plus theme management. Tests override
 * via MockIdentityOptions.permissions to exercise RBAC hiding (§11.1).
 *
 * M7-25: every key here must exist in the backend's code-seeded permission
 * catalogue, and `contract-operation-coverage.test.ts` now enforces that
 * against the seeders themselves. This list is the panel's fake `GET /me`, so
 * a misspelling here does not merely go unnoticed — it CONCEALS the same
 * misspelling in the screens, which is exactly how the plural `wallets.*`
 * keys survived 416 green tests while the live Wallets area was invisible.
 */
export const DEFAULT_ADMIN_PERMISSIONS: ReadonlyArray<string> = [
  "orders.order.view",
  // §3.3 admin transition legs (M6-04): T5/T6 cancel + T4 return.
  "orders.order.cancel",
  "orders.order.transition",
  "dispatch.assign.view",
  // M6-05 dispatch live map: contracted read + quick-assign permissions.
  "dispatch.assign.manage",
  // M6-19 §6.6 override approval queue.
  "dispatch.override.decide",
  // M7-07 §13 dispatch rule catalog (dispatch.yaml admin surface).
  "dispatch.rules.manage",
  "tracking.map.view",
  "geo.zone.view",
  // M7-220: the seeded write keys (GeoPermissionSeeder) — the zone editor now
  // gates create/edit and pricing on them.
  "geo.zone.manage",
  "geo.pricing.manage",
  // M6-19 geo.yaml v1.2.0 — per-scope geofence radius overrides.
  "geo.geofence.manage",
  "merchants.merchant.view",
  // M7-43 — merchant create/status writes (seeded since M2-06, first granted
  // to the mock admin here alongside the Wave-8 onboarding surfaces).
  "merchants.merchant.manage",
  // M7-42/M7-43 — the §14.5 join-flow review queue (merchants.yaml v1.4.0).
  "merchants.application.view",
  "merchants.application.decide",
  // M7-38 §7.2 cross-merchant engagement reports (merchants.yaml admin surface).
  "merchants.engagement.view_all",
  // M7-147/M7-148 — the admin-managed store categories (merchants.yaml v1.11.0).
  "merchants.category.view",
  "merchants.category.manage",
  "drivers.driver.view",
  // M7-23 deep driver management (drivers.yaml admin surface): profile
  // create/update, the document review queue, vehicles.
  "drivers.driver.manage",
  "drivers.document.view",
  "drivers.document.review",
  "drivers.vehicle.manage",
  // M7-150 — the admin-managed vehicle types (drivers.yaml v1.5.0). Seeded
  // to every role already holding drivers.driver.manage.
  "drivers.vehicle_type.view",
  "drivers.vehicle_type.manage",
  "customers.customer.view",
  // M7-23 — the display-field edit contracted since customers.yaml v1.0.0.
  "customers.customer.manage",
  // M6-06 money screens: wallets / settlement / subscriptions admin surfaces.
  "wallet.wallet.view",
  "wallet.ledger.view",
  "wallet.wallet.fund",
  "wallet.wallet.adjust",
  "wallet.wallet.set_credit_limit",
  "wallet.wallet.override_limit",
  "wallet.driver_plan.view",
  // M7-23 §8.2 plan change (daily subscription vs commission) — the write
  // that walletsApi has always exposed and no screen ever called.
  "wallet.driver_plan.manage",
  "settlement.record.view",
  "settlement.reconciliation.view",
  "settlement.reconciliation.generate",
  "subscriptions.subscription.view",
  "subscriptions.subscription.manage",
  "subscriptions.package.view",
  "subscriptions.package.manage",
  "subscriptions.invoice.manage",
  // M6-18 audit READ surface (audit.yaml v1.0.0) — granted to the admin role.
  "identity.audit.view",
  // M6-07 promotions / platform config / provider registry admin surfaces.
  "promotions.promotion.view",
  "promotions.promotion.manage",
  "promotions.application.view",
  "promotions.report.view",
  "promotions.evaluation.execute",
  // M7-106 Wave 14 — customer-app ad banners (marketing.yaml admin surface).
  "marketing.banner.view",
  "marketing.banner.manage",
  // M7-301 — pop-up ads (marketing.yaml v1.7.0): one key for the whole surface.
  "marketing.popup.manage",
  // M7-309 — the privacy policy (admin legal surface): one key, read + write.
  "platform.legal.manage",
  "notifications.template.view",
  // M6-19 notifications admin screen: content edits + internal test-send.
  "notifications.template.manage",
  "notifications.notification.test_send",
  // M7-36/M7-37d v1.2.0 free-form custom send.
  "notifications.notification.custom_send",
  // M7-01 notifications.yaml v1.1.0 delivery log (M7-07 screen).
  "notifications.delivery.view",
  "settings.flag.view",
  "settings.flag.manage",
  "settings.setting.view",
  "settings.setting.manage",
  "payments.provider.view",
  "payments.provider.manage",
  "theming.theme.manage",
  "identity.role.view",
  // M7-23 RBAC management area (identity.yaml roles/permissions/user roles).
  "identity.role.manage",
  "identity.user.view",
  "identity.user.assign_roles",
  // M7-01 analytics.yaml v1.0.0 dashboard aggregate (M7-07 dashboard swap).
  "identity.kpis.view",
];

/* ------------------------------------------------------------------ */
/* Emulated httpOnly cookie jar (mock-only persistence)                */
/* ------------------------------------------------------------------ */

function cookieRead(): string | null {
  try {
    return window.sessionStorage.getItem(EMULATED_COOKIE_KEY);
  } catch {
    return null;
  }
}

function cookieWrite(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(EMULATED_COOKIE_KEY);
    else window.sessionStorage.setItem(EMULATED_COOKIE_KEY, value);
  } catch {
    /* non-fatal, mirrors a blocked cookie */
  }
}

/* ------------------------------------------------------------------ */
/* Contract-faithful behavior                                          */
/* ------------------------------------------------------------------ */

const ERROR_MESSAGES: Record<string, { readonly ar: string; readonly en: string }> = {
  VALIDATION_FAILED: { ar: "البيانات المدخلة غير صالحة", en: "The submitted data is invalid" },
  AUTH_INVALID_CREDENTIALS: { ar: "بيانات الدخول غير صحيحة", en: "Invalid credentials" },
  AUTH_TOKEN_INVALID: { ar: "جلسة غير صالحة، الرجاء تسجيل الدخول مجدداً", en: "Invalid session, please sign in again" },
  REFRESH_INVALID: { ar: "جلسة غير صالحة", en: "Invalid session" },
  REFRESH_REUSE_DETECTED: { ar: "تم إنهاء الجلسة لأسباب أمنية", en: "Session terminated for security reasons" },
  RATE_LIMITED: { ar: "طلبات كثيرة، حاول لاحقاً", en: "Too many requests, try again later" },
  IDEMPOTENCY_KEY_MISSING: { ar: "طلب غير صالح (مفتاح التكرار مفقود)", en: "Invalid request (missing idempotency key)" },
  RESOURCE_NOT_FOUND: { ar: "العنصر غير موجود", en: "Resource not found" },
};

interface MockState {
  tokenSerial: number;
  /** Access tokens currently accepted by /me and /auth/logout. */
  validAccessTokens: Set<string>;
  /** Rotated-away refresh tokens — presenting one is treated as theft. */
  usedRefreshTokens: Set<string>;
  /** Consecutive failed password logins (gateway throttling emulation). */
  failedLogins: number;
  /** Epoch ms until which /auth/login answers 429 RATE_LIMITED. */
  lockedUntilMs: number;
  /**
   * Client type of the active session chain — decides body-vs-cookie token
   * delivery (the real backend knows this from the chain row). Defaults to
   * ADMIN_WEB: this mock IS the Admin Panel's backend, and a seeded cookie
   * (tests) belongs to an ADMIN_WEB chain by definition.
   */
  chainClientType: string;
}

export interface MockIdentityOptions {
  /** Simulated network latency (default 350 ms; pass 0 in tests). */
  readonly latencyMs?: number;
  /** Effective permissions of /me (default: DEFAULT_ADMIN_PERMISSIONS) — RBAC test seam. */
  readonly permissions?: ReadonlyArray<string>;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function userBody(): User {
  return {
    // Same ULID the user-directory mock seeds for this admin (M7-183): `/me`
    // and `GET /admin/users` must describe ONE person, as they do in production.
    id: "01J6M3ADMINUSER00000000001",
    phone: MOCK_ADMIN.phone,
    name: MOCK_ADMIN.name,
    email: null,
    status: "ACTIVE",
    preferred_locale: "ar",
    phone_verified: true,
    created_at: "2026-07-01T09:00:00Z",
  };
}

function meBody(permissions: ReadonlyArray<string>): Me {
  return {
    ...userBody(),
    roles: [{ role_key: "platform_admin", scope_type: "GLOBAL", scope_id: null }],
    permissions,
  };
}

export function createMockIdentityTransport(options: MockIdentityOptions = {}): Transport {
  const latency = options.latencyMs ?? 350;
  const permissions = options.permissions ?? DEFAULT_ADMIN_PERMISSIONS;
  const state: MockState = {
    tokenSerial: 0,
    validAccessTokens: new Set(),
    usedRefreshTokens: new Set(),
    failedLogins: 0,
    lockedUntilMs: 0,
    chainClientType: "ADMIN_WEB",
  };

  /**
   * Issues a rotated pair per v1.1.0: ADMIN_WEB chains get the refresh token
   * as the (emulated) httpOnly cookie and NO `refresh_token` in the body;
   * other client types keep the v1.0.0 body pair.
   */
  function issuePair(): TokenPair {
    state.tokenSerial += 1;
    const serial = state.tokenSerial;
    const access = `mock-access-${String(serial)}`;
    const refresh = `mock-refresh-${String(serial)}-${"x".repeat(24)}`; // ≥32 chars per contract
    state.validAccessTokens.add(access);
    cookieWrite(refresh);
    if (state.chainClientType === "ADMIN_WEB") {
      // v1.1.0: Set-Cookie only — the body OMITS refresh_token.
      return { access_token: access, token_type: "Bearer", expires_in: ACCESS_TTL_SECONDS };
    }
    return {
      access_token: access,
      token_type: "Bearer",
      expires_in: ACCESS_TTL_SECONDS,
      refresh_token: refresh,
    };
  }

  function error(
    status: number,
    code: string,
    locale: "ar" | "en",
    details?: unknown,
    headers?: Readonly<Record<string, string>>,
  ): TransportResponse {
    const messages = ERROR_MESSAGES[code] ?? { ar: code, en: code };
    return {
      status,
      body: {
        error: {
          code,
          message: messages[locale],
          ...(details !== undefined ? { details } : {}),
          trace_id: `mock-${crypto.randomUUID()}`,
        },
      },
      ...(headers !== undefined ? { headers } : {}),
    };
  }

  function localeOf(request: TransportRequest): "ar" | "en" {
    return request.headers["Accept-Language"] === "en" ? "en" : "ar";
  }

  function bearerOf(request: TransportRequest): string | null {
    const header = request.headers.Authorization;
    return header?.startsWith("Bearer ") === true ? header.slice(7) : null;
  }

  function rateLimited(locale: "ar" | "en"): TransportResponse {
    const remaining = Math.max(1, Math.ceil((state.lockedUntilMs - Date.now()) / 1000));
    return error(429, "RATE_LIMITED", locale, undefined, { "retry-after": String(remaining) });
  }

  function handleLogin(request: TransportRequest, locale: "ar" | "en"): TransportResponse {
    if (Date.now() < state.lockedUntilMs) return rateLimited(locale);
    const body = request.body as { phone?: unknown; password?: unknown; client_type?: unknown };
    const details: Array<{ field: string; code: string; message: string }> = [];
    if (typeof body.phone !== "string" || !PHONE_PATTERN.test(body.phone)) {
      details.push({ field: "phone", code: "INVALID_FORMAT", message: locale === "ar" ? "رقم هاتف غير صالح" : "Invalid phone number" });
    }
    if (typeof body.password !== "string" || body.password.length < 8) {
      details.push({ field: "password", code: "TOO_SHORT", message: locale === "ar" ? "كلمة مرور قصيرة جداً" : "Password too short" });
    }
    if (details.length > 0) return error(400, "VALIDATION_FAILED", locale, details);
    if (body.phone !== MOCK_ADMIN.phone || body.password !== MOCK_ADMIN.password) {
      state.failedLogins += 1;
      if (state.failedLogins >= LOGIN_LOCKOUT_THRESHOLD) {
        state.lockedUntilMs = Date.now() + LOGIN_LOCKOUT_SECONDS * 1000;
        state.failedLogins = 0;
        return rateLimited(locale);
      }
      // Same code for unknown phone vs wrong password — no enumeration (§11.1).
      return error(401, "AUTH_INVALID_CREDENTIALS", locale);
    }
    state.failedLogins = 0;
    state.lockedUntilMs = 0;
    state.chainClientType = typeof body.client_type === "string" ? body.client_type : "ADMIN_WEB";
    const result: AuthResult = { user: userBody(), tokens: issuePair(), is_new_user: false };
    return { status: 200, body: result };
  }

  function handleRefresh(request: TransportRequest, locale: "ar" | "en"): TransportResponse {
    const body = request.body as { refresh_token?: unknown };
    // v1.1.0: body OR httpOnly cookie — body wins when both are present.
    const fromBody = typeof body.refresh_token === "string" && body.refresh_token !== "" ? body.refresh_token : null;
    const presented = fromBody ?? cookieRead();
    if (presented === null || presented === "") {
      return error(401, "REFRESH_INVALID", locale); // neither body nor cookie
    }
    if (state.usedRefreshTokens.has(presented)) {
      // Reuse = theft: revoke the whole chain (contract behavior).
      cookieWrite(null);
      state.validAccessTokens.clear();
      return error(401, "REFRESH_REUSE_DETECTED", locale);
    }
    if (presented !== cookieRead()) {
      return error(401, "REFRESH_INVALID", locale);
    }
    state.usedRefreshTokens.add(presented);
    return { status: 200, body: issuePair() };
  }

  function handleLogout(request: TransportRequest, locale: "ar" | "en"): TransportResponse {
    const access = bearerOf(request);
    if (access === null || !state.validAccessTokens.has(access)) {
      return error(401, "AUTH_TOKEN_INVALID", locale);
    }
    state.validAccessTokens.clear();
    state.usedRefreshTokens.clear();
    cookieWrite(null);
    return { status: 204, body: null };
  }

  function handleMe(request: TransportRequest, locale: "ar" | "en"): TransportResponse {
    const access = bearerOf(request);
    if (access === null || !state.validAccessTokens.has(access)) {
      return error(401, "AUTH_TOKEN_INVALID", locale);
    }
    return { status: 200, body: meBody(permissions) };
  }

  return async (request) => {
    if (latency > 0) await sleep(latency);
    const locale = localeOf(request);
    if (request.method !== "GET" && request.headers["Idempotency-Key"] === undefined) {
      return error(400, "IDEMPOTENCY_KEY_MISSING", locale);
    }
    const route = `${request.method} ${request.path}`;
    switch (route) {
      case "POST /auth/login":
        return handleLogin(request, locale);
      case "POST /auth/token/refresh":
        return handleRefresh(request, locale);
      case "POST /auth/logout":
        return handleLogout(request, locale);
      case "GET /me":
        return handleMe(request, locale);
      default:
        return error(404, "RESOURCE_NOT_FOUND", locale);
    }
  };
}
